import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import SchoolTwoToneIcon from '@mui/icons-material/SchoolTwoTone';
import AddToHomeScreenTwoToneIcon from '@mui/icons-material/AddToHomeScreenTwoTone';
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '../../components'
import {
  AppLogo,
  CompanyLogo,
  GlobalDownload,
  EulaModal,
  GlobalFeedback,
  GlobalReset,
  GlobalSettings,
  GlobalUpload,
  AddToHomeScreenModal,
} from './components'

import { RenderIf } from '../../utils/fns'
import { useModal, useWindowSize } from '../../utils/hooks'
import {
  GLOBAL_SETTINGS_FONTSIZE_OPTIONS,
  GLOBAL_SETTINGS_ORIENTATION_OPTIONS,
  TOP_NAVIGATION_MODAL,
} from '../../utils/constants'

import { globalSettingsActions, selectGlobalSettings } from '../../utils/slices/settings.slice'

const TopNavigation = (props) => {
  const {
    appConfig,
    about: About,
    print: Print,
    companyLogoProps,
    downloadProps,
    uploadProps,
    settingsProps,
    feedbackProps,
  } = props
  const { appCode, appName, navigationConfig, logoUrl } = appConfig

  const dispatch = useDispatch()
  const { mobileNav, isEulaAccepted } = useSelector(selectGlobalSettings)

  const [show, toggleModal] = useModal(null)

  const { width } = useWindowSize()

  const handleTutorialAction = () => {
    const tutorialTriggerElement = document.querySelector('div.inmplayer-trigger')
    const evt = new MouseEvent('click', {
      bubbles: false,
      cancelable: true,
      view: window,
    })
    tutorialTriggerElement.dispatchEvent(evt)
  }

  const handleMobileNav = () => {
    dispatch(globalSettingsActions.updateGlobalSettings({ mobileNav: !mobileNav }))

    if (mobileNav) document.documentElement.classList.remove('-mobileNavOpen')
    else document.documentElement.classList.add('-mobileNavOpen')
  }

  useEffect(() => {
    if (!isEulaAccepted) {
      toggleModal(TOP_NAVIGATION_MODAL.EULA)
    }
  }, [])

  useEffect(() => {
    if (width >= 768) {
      dispatch(globalSettingsActions.updateGlobalSettings({ mobileNav: false }))
      document.documentElement.classList.remove('-mobileNavOpen')
    }
  }, [width])

  return (
    <React.Fragment>
      <header className="topNav">
        <CompanyLogo {...companyLogoProps} />
        <div className="topNav__buttonGroup">
          <GlobalDownload {...downloadProps} />
          <GlobalUpload {...uploadProps} appCode={appConfig.appCode} />
          <GlobalReset />

          <Print />
          <About />
          <GlobalSettings {...settingsProps} />
          <GlobalFeedback appConfig={appConfig} {...feedbackProps} />

          {RenderIf(
            navigationConfig?.showTutorial,
            <Button
              variant="topNav"
              className="button topNav__button -tutorials"
              onClick={handleTutorialAction}
            >
              <SchoolTwoToneIcon className='material-icons-two-tone' />
              <span className="button__text">Tutorials</span>
            </Button>,
          )}

          <AppLogo appCode={appConfig.appCode} logoUrl={appConfig.logoUrl} />

          <Button
            variant="topNav"
            className="button topNav__button -homeScreen"
            onClick={() => toggleModal(TOP_NAVIGATION_MODAL.ADD_TO_HOME_SCREEN)}
          >
            <AddToHomeScreenTwoToneIcon className='material-icons-two-tone material-icons-two-tone--noinvert' />
            <span className="button__text">Add to Home Screen</span>
          </Button>
        </div>

        {RenderIf(
          show === TOP_NAVIGATION_MODAL.ADD_TO_HOME_SCREEN,
          <AddToHomeScreenModal
            appName={appName}
            show={show === TOP_NAVIGATION_MODAL.ADD_TO_HOME_SCREEN}
            onHide={toggleModal}
          />,
        )}
        {RenderIf(
          show === TOP_NAVIGATION_MODAL.EULA,
          <EulaModal
            appName={appName}
            show={show === TOP_NAVIGATION_MODAL.EULA}
            onHide={toggleModal}
          />,
        )}

        <AppLogo appCode={appCode} logoUrl={logoUrl} />

        <button className="mobileNavigation" onClick={handleMobileNav} aria-label="Menu">
          <span className="material-icons mobileNavigation__menu">menu</span>
          <span className="material-icons mobileNavigation__close">close</span>
        </button>
      </header>
      <div className="headerShadow"></div>
    </React.Fragment>
  )
}

TopNavigation.propTypes = {
  appConfig: PropTypes.shape({
    appCode: PropTypes.string.isRequired,
    appVersion: PropTypes.string.isRequired,
    appName: PropTypes.string.isRequired,
    logoUrl: PropTypes.string.isRequired,
    gaEventName: PropTypes.string.isRequired,
    downloadFileName: PropTypes.string.isRequired,
    navigationConfig: PropTypes.shape({
      showTutorial: PropTypes.bool,
    }),
  }),
  about: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.element]).isRequired,
  print: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.element]).isRequired,
  companyLogoProps: PropTypes.shape({
    disabled: PropTypes.bool,
    className: PropTypes.string,
  }),
  downloadProps: PropTypes.shape({
    disabled: PropTypes.bool,
    className: PropTypes.string,
    onClick: PropTypes.func,
  }),
  uploadProps: PropTypes.shape({
    disabled: PropTypes.bool,
    className: PropTypes.string,
    onClick: PropTypes.func,
  }),
  printProps: PropTypes.shape({
    disabled: PropTypes.bool,
    Component: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.element]).isRequired,
  }),
  settingsProps: PropTypes.shape({
    fontSizeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    orientationOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.element]),
  }),
  feedbackProps: PropTypes.shape({
    onSubmit: PropTypes.func,
  }),
}

TopNavigation.defaultProps = {
  settingsProps: {
    fontSizeOptions: GLOBAL_SETTINGS_FONTSIZE_OPTIONS,
    orientationOptions: GLOBAL_SETTINGS_ORIENTATION_OPTIONS,
  },
}

export default TopNavigation
